const base = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/api`;

export class ApiError extends Error {}

export type ApiProgressEvent<T = unknown> = {
  type: 'status' | 'provider_response' | 'complete' | 'error';
  stage?: string;
  message: string;
  detail?: string;
  provider?: 'gemini';
  result?: T;
  at?: string;
};

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(`${base}${path}`, { ...init, headers, credentials: 'same-origin' });
  if (response.status === 204) return undefined as T;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(data.error || '요청을 처리하지 못했습니다.');
  return data as T;
}

export async function streamApi<T>(path: string, init: RequestInit, onEvent: (event: ApiProgressEvent<T>) => void): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(`${base}${path}`, { ...init, headers, credentials: 'same-origin' });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(data.error || '요청을 시작하지 못했습니다.');
  }
  if (!response.body) throw new ApiError('진행 상황 스트림을 열지 못했습니다.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: T | undefined;
  let streamError: string | null = null;
  const consumeLine = (line: string) => {
    if (!line.trim()) return;
    const event = JSON.parse(line) as ApiProgressEvent<T>;
    onEvent(event);
    if (event.type === 'complete') result = event.result;
    if (event.type === 'error') streamError = event.message;
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.forEach(consumeLine);
    if (done) break;
  }
  consumeLine(buffer);
  if (streamError) throw new ApiError(streamError);
  if (result === undefined) throw new ApiError('작업은 종료됐지만 완료 응답을 받지 못했습니다.');
  return result;
}

export const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });
